/**
 * Backlog バグ起票（AI ドラフト → 人手確認 → 起票）の共有ドメイン型と空値ファクトリ。フィールド ID・選択肢は
 * bug-ticket-fields.ts のカタログを、型はここを単一 source とし、product-api（ドメイン / スキーマ / ゲートウェイ）と
 * PIL（起票モーダル / 型 / フック）が同じ型・キー・初期値を参照する（package 間の再宣言による drift を防ぐ）。
 *
 * @graph-stack ops-core
 * @graph-domain Operations
 * @graph-business ops 依頼からのバグ起票フロー（ドラフト・起票入力・起票結果）の共有型・フィールドキー・空値ファクトリ。API と PIL とテストが 1 箇所を参照する
 * @graph-connects none
 */
import { OPS_BUG_DISCOVERY_CHANNEL_FROM_OPS, OPS_BUG_RESPONSIBLE_FIELDS, OPS_BUG_SECTION_FIELDS, } from './bug-ticket-fields.js';
/**
 * AI が確信を持てず人手確認を促すフィールドのキー（タイトル + セクション + メタ）。needsReview / モーダルの
 * ハイライトの語彙で、zod・AI スキーマの enum もこの配列から生成する（唯一の source）。
 * @graph-connects none
 */
export const OPS_BUG_TICKET_FIELD_KEYS = [
    'title',
    // セクション（OPS_BUG_SECTION_FIELDS の key と一致。下の型ガードで drift を検出）。
    'summary',
    'expected',
    'actual',
    'stepsToReproduce',
    'discoveryRoute',
    'cause',
    'workaround',
    'permanentFix',
    'recurrencePrevention',
    'extractionSql',
    // メタ（リスト / 数値 / テキスト系カスタムフィールド）。
    'urgency',
    'discoveryChannel',
    'incidentLevel',
    'causeClass',
    'repo',
    'affectedUserCount',
    'responsibleDiv',
    'responsibleGroup',
    'responsibleTeam',
    'business',
    'causalTicket',
];
/** 空のセクション本文（全 key を空文字に）。モーダル初期値・テスト・ドラフト未取得時の土台に使う。 @graph-connects none */
export function emptyOpsBugSections() {
    const sections = {};
    for (const field of OPS_BUG_SECTION_FIELDS)
        sections[field.key] = '';
    return sections;
}
/**
 * Responsible 組織フィールドの metaKey ↔ 人手確認キー ↔ カタログ定義の対応（単一 source）。API の起因案件補完
 * （field id で Backlog を引く）と PIL のドロップダウン（options を出す）が同じ対応を参照し、キー名の drift を防ぐ。
 * @graph-connects none
 */
export const OPS_BUG_RESPONSIBLE_META = [
    {
        metaKey: 'responsibleDivOptionId',
        reviewKey: 'responsibleDiv',
        field: OPS_BUG_RESPONSIBLE_FIELDS.div,
    },
    {
        metaKey: 'responsibleGroupOptionId',
        reviewKey: 'responsibleGroup',
        field: OPS_BUG_RESPONSIBLE_FIELDS.group,
    },
    {
        metaKey: 'responsibleTeamOptionId',
        reviewKey: 'responsibleTeam',
        field: OPS_BUG_RESPONSIBLE_FIELDS.team,
    },
    {
        metaKey: 'businessOptionId',
        reviewKey: 'business',
        field: OPS_BUG_RESPONSIBLE_FIELDS.business,
    },
];
/**
 * 空のメタ（option id を null に）。モーダル初期値・テスト・ドラフト未取得時の土台に使う。発見の経路（202610新運用）
 * だけは ops 依頼からの起票なら常に CS / オペタン経由なので既定値（`OPS_BUG_DISCOVERY_CHANNEL_FROM_OPS`）を入れる
 * （AI に推定させず決定論で埋める）。
 * @graph-connects none
 */
export function emptyOpsBugMeta() {
    return {
        urgencyOptionId: null,
        discoveryChannelOptionId: OPS_BUG_DISCOVERY_CHANNEL_FROM_OPS,
        incidentLevelOptionId: null,
        causeClassOptionId: null,
        repoOptionId: null,
        affectedUserCount: null,
        responsibleDivOptionId: null,
        responsibleGroupOptionId: null,
        responsibleTeamOptionId: null,
        businessOptionId: null,
        causalIssueKey: null,
    };
}
//# sourceMappingURL=bug-ticket-model.js.map